<template>
  <div class="debugger-page container-fluid py-4">
    <!-- Breadcrumb -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-0" style="font-size: 24px; line-height: 24px; font-weight: 900;">
          <li class="breadcrumb-item" style="color: #435666;" aria-current="page">
            <i class="fa-solid fa-bug text-info me-2"></i>
            <span>Tester Debugger Panel</span>
          </li>
        </ol>
      </nav>
      <span class="badge bg-warning text-dark px-3 py-2 fw-bold shadow-sm">
        <i class="fa-solid fa-triangle-exclamation me-1"></i> NON-PRODUCTION ONLY ENVIRONMENT
      </span>
    </div>

    <!-- Top-Level Tabs -->
    <ul class="nav nav-tabs mb-4" id="mainDebuggerTabs" role="tablist">
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: mainActiveTab === 'tests' }" 
          @click="mainActiveTab = 'tests'" 
          type="button"
        >
          <i class="fa-solid fa-bell-concierge me-2"></i>Reminder & Expiry Tests
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: mainActiveTab === 'query' }" 
          @click="mainActiveTab = 'query'" 
          type="button"
        >
          <i class="fa-solid fa-database me-2"></i>Database Query Console
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: mainActiveTab === 'truncate' }" 
          @click="mainActiveTab = 'truncate'" 
          type="button"
        >
          <i class="fa-solid fa-trash-can me-2 text-danger"></i>Truncate Data
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: mainActiveTab === 'logs' }" 
          @click="mainActiveTab = 'logs'; fetchXodoLogs()" 
          type="button"
        >
          <i class="fa-solid fa-file-invoice me-2 text-warning"></i>Xodo Error Logs
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: mainActiveTab === 'authLogs' }" 
          @click="mainActiveTab = 'authLogs'; fetchAuthLogs()" 
          type="button"
        >
          <i class="fa-solid fa-shield-halved me-2 text-info"></i>Auth & Security Logs
        </button>
      </li>
    </ul>

    <!-- Tab 1: Reminder & Expiry Tests -->
    <div v-show="mainActiveTab === 'tests'" class="tab-pane fade show active">
      <div class="row">
      <!-- Proposals Section -->
      <div class="col-lg-6 mb-4">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h5 class="fw-bold mb-1" style="color: #435666;">
                <i class="fa-solid fa-file-contract text-info me-2"></i>Proposal Reminder / Expiration Test
              </h5>
              <p class="text-muted small mb-0">Prepare proposals to trigger expiration or reminders upon cron executions.</p>
            </div>
            <button @click="loadProposals" class="btn btn-outline-secondary btn-sm" :disabled="loadingProposals" title="Refresh Dropdown">
              <i class="fa-solid fa-sync" :class="{ 'fa-spin': loadingProposals }"></i>
            </button>
          </div>
          <div class="card-body">
            <div class="mb-4">
              <label class="form-label fw-bold text-secondary">Select Target Proposal</label>
              <select class="form-select form-select-lg mb-3" v-model="selectedProposalUuid" @change="onProposalChange" :disabled="loadingProposals">
                <option value="">-- Choose from 100 Recent Proposals --</option>
                <option v-for="p in proposals" :key="p.id" :value="p.id">
                  {{ p.Lead?.organisation_name || 'No Org' }}{{ getProposalAcceptanceOrg(p) ? ` [Accepted: ${getProposalAcceptanceOrg(p)}]` : '' }} - {{ p.proposal_owner || 'Owner' }} ({{ p.id.substring(0, 8) }}) {{ p.shared_users && p.shared_users.length ? `[Shared: ${p.shared_users.length}]` : '' }}
                </option>
              </select>

              <div class="text-center my-2 text-muted">OR ENTER UUID MANUALLY</div>
              <input type="text" class="form-control" v-model="manualProposalUuid" @input="onManualProposalInput" placeholder="Enter proposal UUID (e.g. b6293640-0b2c-4945-801d-6448f1134c73)">
            </div>

            <!-- Details Display -->
            <transition name="fade">
              <div v-if="activeProposal" class="details-box p-3 rounded mb-4">
                <h6 class="fw-bold border-bottom pb-2 mb-2 text-secondary">Selected Proposal Info</h6>
                <div class="row g-2 small text-dark">
                  <div class="col-5 text-muted">Organization:</div>
                  <div class="col-7 fw-bold">
                    {{ activeProposal.Lead?.organisation_name || 'N/A' }}
                    <span v-if="getProposalAcceptanceOrg(activeProposal)" class="text-success small d-block">
                      <i class="fa-solid fa-check-double me-1"></i>Accepted as: {{ getProposalAcceptanceOrg(activeProposal) }}
                    </span>
                  </div>

                  <div class="col-5 text-muted">Primary Contact:</div>
                  <div class="col-7 fw-bold">
                    {{ getProposalPrimaryContact(activeProposal) || 'N/A' }}
                  </div>
                  
                  <div class="col-5 text-muted">Owner Email:</div>
                  <div class="col-7">{{ activeProposal.proposal_owner || 'N/A' }}</div>

                  <div class="col-5 text-muted">Lead Email:</div>
                  <div class="col-7 text-info fw-bold">{{ activeProposal.Lead?.email || 'N/A' }}</div>

                  <div class="col-5 text-muted">Expiry Date:</div>
                  <div class="col-7">{{ formatDate(activeProposal.expiry_date) }}</div>

                  <div class="col-5 text-muted">Next Reminder At:</div>
                  <div class="col-7">{{ activeProposal.next_reminder_at ? formatDate(activeProposal.next_reminder_at) : 'None' }}</div>

                  <div class="col-5 text-muted">Shared Viewers:</div>
                  <div class="col-7">
                    <span v-if="!activeProposal.shared_users || !activeProposal.shared_users.length" class="text-muted">None</span>
                    <div v-else v-for="su in activeProposal.shared_users" :key="su.id" class="badge bg-secondary me-1 mb-1">
                      {{ su.email }}
                    </div>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Actions -->
            <div class="d-flex flex-column gap-2 mt-4">
              <button @click="prepareProposalReminder" class="btn btn-info text-white fw-bold py-2 btn-action" :disabled="!activeProposalUuid || runningAction">
                <i class="fa-solid fa-bell me-2"></i> Prepare for Reminder Test
              </button>
              <button @click="prepareProposalExpiry" class="btn btn-warning text-dark fw-bold py-2 btn-action" :disabled="!activeProposalUuid || runningAction">
                <i class="fa-solid fa-hourglass-end me-2"></i> Prepare for Expiration Test
              </button>
              <button @click="resetProposal" class="btn btn-outline-danger fw-bold py-2 btn-action" :disabled="!activeProposalUuid || runningAction">
                <i class="fa-solid fa-undo me-2"></i> Revert & Restore State
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Contracts Section -->
      <div class="col-lg-6 mb-4">
        <div class="card h-100 border-0 shadow-sm">
          <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h5 class="fw-bold mb-1" style="color: #435666;">
                <i class="fa-solid fa-file-signature text-info me-2"></i>Contract Reminder Test
              </h5>
              <p class="text-muted small mb-0">Force contract stale status and backdate reminder times for cron triggers.</p>
            </div>
            <button @click="loadContracts" class="btn btn-outline-secondary btn-sm" :disabled="loadingContracts" title="Refresh Dropdown">
              <i class="fa-solid fa-sync" :class="{ 'fa-spin': loadingContracts }"></i>
            </button>
          </div>
          <div class="card-body">
            <div class="mb-4">
              <label class="form-label fw-bold text-secondary">Select Target Contract</label>
              <select class="form-select form-select-lg mb-3" v-model="selectedContractUuid" @change="onContractChange" :disabled="loadingContracts">
                <option value="">-- Choose from 100 Recent Contracts --</option>
                <option v-for="c in contracts" :key="c.id" :value="c.id">
                  {{ c.Lead?.organisation_name || 'No Org' }}{{ getContractAcceptanceOrg(c) ? ` [Accepted: ${getContractAcceptanceOrg(c)}]` : '' }} ({{ c.id.substring(0, 8) }}) [Signers: {{ getContractSigners(c).length }}]
                </option>
              </select>

              <div class="text-center my-2 text-muted">OR ENTER UUID MANUALLY</div>
              <input type="text" class="form-control" v-model="manualContractUuid" @input="onManualContractInput" placeholder="Enter contract UUID (e.g. 4554c3d7-3fdd-4bee-9ff2-63df09aa4dc2)">
            </div>

            <!-- Details Display -->
            <transition name="fade">
              <div v-if="activeContract" class="details-box p-3 rounded mb-4">
                <h6 class="fw-bold border-bottom pb-2 mb-2 text-secondary">Selected Contract Info</h6>
                <div class="row g-2 small text-dark">
                  <div class="col-5 text-muted">Organization:</div>
                  <div class="col-7 fw-bold">
                    {{ activeContract.Lead?.organisation_name || 'N/A' }}
                    <span v-if="getContractAcceptanceOrg(activeContract)" class="text-success small d-block">
                      <i class="fa-solid fa-check-double me-1"></i>Accepted as: {{ getContractAcceptanceOrg(activeContract) }}
                    </span>
                  </div>

                  <div class="col-5 text-muted">Primary Contact:</div>
                  <div class="col-7 fw-bold">
                    {{ getContractPrimaryContact(activeContract) || 'N/A' }}
                  </div>

                  <div class="col-5 text-muted">Lead Email:</div>
                  <div class="col-7 text-info fw-bold">{{ activeContract.Lead?.email || 'N/A' }}</div>

                  <div class="col-5 text-muted">Status:</div>
                  <div class="col-7">
                    <span class="badge" :class="getContractStatusClass(activeContract.contract_status)">
                      {{ getContractStatusText(activeContract.contract_status) }}
                    </span>
                  </div>

                  <div class="col-5 text-muted">Next Reminder At:</div>
                  <div class="col-7">{{ activeContract.next_reminder_at ? formatDate(activeContract.next_reminder_at) : 'None' }}</div>

                  <div class="col-5 text-muted">Signers:</div>
                  <div class="col-7">
                    <span v-if="!getContractSigners(activeContract).length" class="text-muted">None</span>
                    <div v-else v-for="signer in getContractSigners(activeContract)" :key="signer.email" class="mb-1">
                      <span class="badge bg-primary me-1">{{ [signer.first_name || signer.firstName, signer.last_name || signer.lastName].filter(Boolean).join(' ') || signer.name }}</span>
                      <span class="text-muted small">({{ signer.email }})</span>
                    </div>
                  </div>

                  <div class="col-5 text-muted">Shared Signers/Viewers:</div>
                  <div class="col-7">
                    <span v-if="!activeContract.shared_users || !activeContract.shared_users.length" class="text-muted">None</span>
                    <div v-else v-for="su in activeContract.shared_users" :key="su.id" class="mb-1">
                      <span class="badge bg-secondary me-1">{{ su.email }}</span>
                      <span class="badge bg-light text-dark small border">{{ su.share_type }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Actions -->
            <div class="d-flex flex-column gap-2 mt-4">
              <button @click="prepareContractReminder" class="btn btn-info text-white fw-bold py-2 btn-action" :disabled="!activeContractUuid || runningAction">
                <i class="fa-solid fa-bell me-2"></i> Prepare for Reminder Test
              </button>
              <button @click="resetContract" class="btn btn-outline-danger fw-bold py-2 btn-action" :disabled="!activeContractUuid || runningAction">
                <i class="fa-solid fa-undo me-2"></i> Revert & Restore State
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Tab 2: Database Query Console -->
    <div v-show="mainActiveTab === 'query'" class="tab-pane fade show active">
      <!-- Direct SQL Select Tool -->
      <div class="row">
      <div class="col-12">
        <div class="card border-0 shadow-sm">
          <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
            <div>
              <h5 class="fw-bold mb-1" style="color: #435666;">
                <i class="fa-solid fa-database text-info me-2"></i>Direct SQL Query Console
              </h5>
              <p class="text-muted small mb-0">Execute SELECT, SHOW, DESCRIBE, or EXPLAIN queries to investigate database state directly.</p>
            </div>
            <button @click="loadSchema" class="btn btn-outline-secondary btn-sm" :disabled="loadingSchema" title="Reload Database Schema">
              <i class="fa-solid fa-sync" :class="{ 'fa-spin': loadingSchema }"></i> Refresh Schema
            </button>
          </div>
          
          <div class="card-body">
            <div class="row">
              <!-- Left Side: Schema Explorer -->
              <div class="col-md-3 border-end">
                <h6 class="fw-bold text-secondary mb-3">
                  <i class="fa-solid fa-sitemap me-2"></i>Schema Explorer
                </h6>
                <div v-if="loadingSchema" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-info" role="status">
                    <span class="visually-hidden">Loading schema...</span>
                  </div>
                  <div class="small text-muted mt-2">Loading database tables...</div>
                </div>
                <div v-else class="schema-list" style="max-height: 500px; overflow-y: auto;">
                  <div v-for="table in dbSchema" :key="table.name" class="table-node mb-2">
                    <div class="d-flex justify-content-between align-items-center p-2 rounded cursor-pointer hover-bg-light" @click="toggleTable(table.name)">
                      <span class="fw-bold text-dark small">
                        <i class="fa-solid" :class="expandedTables[table.name] ? 'fa-folder-open text-warning' : 'fa-folder text-warning'"></i>
                        <span class="ms-2">{{ table.name }}</span>
                      </span>
                      <div class="d-flex align-items-center gap-1">
                        <span class="badge bg-light text-secondary border small">{{ table.columns?.length || 0 }} cols</span>
                        <button class="btn btn-link btn-xs text-info p-0" @click.stop="quickQuery(table.name)" title="Quick SELECT">
                          <i class="fa-solid fa-terminal"></i>
                        </button>
                      </div>
                    </div>
                    
                    <transition name="slide-down">
                      <div v-show="expandedTables[table.name]" class="ms-3 border-start ps-2">
                        <div v-for="col in table.columns" :key="col.name" class="d-flex justify-content-between py-1 small border-bottom-dashed">
                          <span class="text-muted text-monospace" @click="insertAtCursor(col.name)" title="Click to insert column" style="cursor: pointer; font-size: 11px;">{{ col.name }}</span>
                          <span class="text-secondary font-italic font-size-xs">{{ col.type }}</span>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>

              <!-- Right Side: SQL Console and Results -->
              <div class="col-md-9">
                <!-- Tab Headers -->
                <ul class="nav nav-pills mb-4" id="queryTabs" role="tablist">
                  <li class="nav-item" role="presentation">
                    <button 
                      class="nav-link px-4 py-2 fw-bold" 
                      :class="{ active: activeTab === 'visual' }" 
                      @click="activeTab = 'visual'" 
                      type="button"
                    >
                      <i class="fa-solid fa-square-poll-horizontal me-2"></i>Visual Query Builder
                    </button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button 
                      class="nav-link px-4 py-2 fw-bold" 
                      :class="{ active: activeTab === 'advanced' }" 
                      @click="activeTab = 'advanced'" 
                      type="button"
                    >
                      <i class="fa-solid fa-code me-2"></i>Advanced SQL Console
                    </button>
                  </li>
                </ul>

                <!-- Tab Content -->
                <div class="tab-content" id="queryTabsContent">
                  <!-- Tab 1: Visual Builder -->
                  <div v-show="activeTab === 'visual'" class="tab-pane fade show active">
                    <!-- Table Selection -->
                    <div class="row mb-3">
                      <div class="col-md-5">
                        <label class="form-label fw-bold text-secondary">Target Database Table</label>
                        <select class="form-select form-select-lg" v-model="selectedTable" @change="onTableChange">
                          <option value="">-- Select a Table --</option>
                          <option v-for="table in dbSchema" :key="table.name" :value="table.name">
                            {{ table.name }}
                          </option>
                        </select>
                      </div>
                      
                      <div class="col-md-7" v-if="selectedTable">
                        <label class="form-label fw-bold text-secondary d-flex justify-content-between align-items-center mb-1">
                          <span>Columns to Retrieve</span>
                          <span class="text-xs">
                            <a href="#" @click.prevent="selectAllColumns(true)" class="me-2 text-info small">Select All</a> |
                            <a href="#" @click.prevent="selectAllColumns(false)" class="text-secondary small ms-1">Clear All</a>
                          </span>
                        </label>
                        <div class="border rounded p-2 bg-light select-columns-box d-flex flex-wrap gap-2" style="max-height: 100px; overflow-y: auto;">
                          <div v-for="col in selectedColumns" :key="col.name" class="form-check form-check-inline mb-0">
                            <input class="form-check-input" type="checkbox" :id="'col-' + col.name" v-model="col.checked">
                            <label class="form-check-label small text-monospace" :for="'col-' + col.name" :title="col.type">{{ col.name }}</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="selectedTable">
                      <!-- Filter Conditions (WHERE) -->
                      <div class="mb-3">
                        <label class="form-label fw-bold text-secondary d-flex justify-content-between align-items-center mb-1">
                          <span>Filter Conditions (AND)</span>
                          <button class="btn btn-outline-info btn-sm py-1 px-2 fw-bold" style="font-size: 11px;" @click="addFilter">
                            <i class="fa-solid fa-plus me-1"></i> Add Filter
                          </button>
                        </label>
                        <div v-if="queryFilters.length === 0" class="text-muted small border rounded p-3 text-center bg-light">
                          No active filters. Retrieving all rows.
                        </div>
                        <div v-else class="filters-list d-flex flex-column gap-2">
                          <div v-for="(filter, idx) in queryFilters" :key="idx" class="d-flex align-items-center gap-2 border rounded p-2 bg-light shadow-xs">
                            <!-- Column Selector -->
                            <select class="form-select form-select-sm" v-model="filter.column" style="max-width: 200px;">
                              <option value="">-- Select Column --</option>
                              <option v-for="col in selectedColumns" :key="col.name" :value="col.name">{{ col.name }}</option>
                            </select>
                            
                            <!-- Operator Selector -->
                            <select class="form-select form-select-sm" v-model="filter.operator" style="max-width: 150px;">
                              <option value="=">=</option>
                              <option value="!=">!=</option>
                              <option value="LIKE">LIKE (Contains)</option>
                              <option value=">">&gt;</option>
                              <option value="<">&lt;</option>
                              <option value=">=">&gt;=</option>
                              <option value="<=">&lt;=</option>
                              <option value="IS NULL">IS NULL</option>
                              <option value="IS NOT NULL">IS NOT NULL</option>
                            </select>
                            
                            <!-- Value Input -->
                            <input 
                              v-if="filter.operator !== 'IS NULL' && filter.operator !== 'IS NOT NULL'" 
                              type="text" 
                              class="form-control form-control-sm" 
                              v-model="filter.value" 
                              placeholder="Value..."
                            >
                            <span v-else class="text-muted small font-italic px-3">No value needed</span>

                            <!-- Delete Filter -->
                            <button class="btn btn-outline-danger btn-sm px-2 border-0" @click="removeFilter(idx)" title="Remove filter condition">
                              <i class="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Sorting & Limits (ORDER BY / LIMIT) -->
                      <div class="row mb-3">
                        <div class="col-md-6">
                          <label class="form-label fw-bold text-secondary">Sort By Column</label>
                          <div class="input-group">
                            <select class="form-select" v-model="sortByColumn">
                              <option value="">-- None --</option>
                              <option v-for="col in selectedColumns" :key="col.name" :value="col.name">{{ col.name }}</option>
                            </select>
                            <button 
                              v-if="sortByColumn" 
                              class="btn btn-outline-secondary sort-order-btn" 
                              @click="sortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC'"
                              type="button"
                              title="Toggle sort direction"
                            >
                              <i class="fa-solid" :class="sortOrder === 'ASC' ? 'fa-sort-up mt-1' : 'fa-sort-down mb-1'"></i>
                              <span>{{ sortOrder }}</span>
                            </button>
                          </div>
                        </div>
                        
                        <div class="col-md-3">
                          <label class="form-label fw-bold text-secondary">Row Limit</label>
                          <input type="number" class="form-control" v-model.number="queryLimit" min="1" max="1000">
                        </div>
                      </div>

                      <!-- Live SQL Preview -->
                      <div class="mb-3">
                        <label class="form-label fw-bold text-secondary small mb-1">Generated SQL Preview</label>
                        <div class="p-2 border rounded font-monospace bg-dark text-warning small text-truncate" title="Generated SELECT statement" style="font-size: 12px;">
                          {{ generatedSql || 'No query generated yet.' }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Tab 2: Advanced Console -->
                  <div v-show="activeTab === 'advanced'" class="tab-pane fade show active">
                    <div class="mb-3 position-relative">
                      <label class="form-label fw-bold text-secondary d-flex justify-content-between">
                        <span>SQL Query Console</span>
                        <span class="text-xs text-muted font-monospace">SELECT only. Terminate with semicolon optional.</span>
                      </label>
                      <textarea 
                        class="form-control font-monospace sql-editor" 
                        rows="5" 
                        v-model="dbQuery" 
                        placeholder="-- Enter SELECT query here&#10;SELECT * FROM tbl_leads LIMIT 5;"
                        ref="sqlEditor"
                      ></textarea>
                    </div>
                  </div>
                </div>

                <!-- Query Controls -->
                <div class="d-flex gap-2 mb-3 align-items-center">
                  <button 
                    @click="runQuery" 
                    class="btn btn-info text-white fw-bold px-4 py-2" 
                    :disabled="runningQuery || (activeTab === 'visual' && !selectedTable) || (activeTab === 'advanced' && !dbQuery.trim())"
                  >
                    <span v-if="runningQuery" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    <i v-else class="fa-solid fa-play me-2"></i> Fetch Data
                  </button>
                  <button v-if="activeTab === 'advanced'" @click="clearQuery" class="btn btn-outline-secondary px-3 py-2" :disabled="runningQuery">
                    <i class="fa-solid fa-eraser me-1"></i> Clear
                  </button>
                  <button v-if="activeTab === 'visual' && selectedTable" @click="resetVisualBuilder" class="btn btn-outline-secondary px-3 py-2" :disabled="runningQuery">
                    <i class="fa-solid fa-undo me-1"></i> Reset Fields
                  </button>
                  
                  <div class="dropdown ms-auto" v-if="activeTab === 'advanced'">
                    <button class="btn btn-outline-secondary dropdown-toggle py-2" type="button" id="templatesDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                      <i class="fa-solid fa-lightbulb me-1"></i> Templates
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="templatesDropdown">
                      <li><a class="dropdown-item" href="#" @click.prevent="setTemplate('leads')">Select Recent Leads</a></li>
                      <li><a class="dropdown-item" href="#" @click.prevent="setTemplate('proposals')">Select Active Proposals</a></li>
                      <li><a class="dropdown-item" href="#" @click.prevent="setTemplate('contracts')">Select Executed Contracts</a></li>
                      <li><a class="dropdown-item" href="#" @click.prevent="setTemplate('count')">Count Rows by Status</a></li>
                    </ul>
                  </div>
                </div>

                <!-- Error / Safety Alert -->
                <transition name="fade">
                  <div v-if="queryError" class="alert alert-danger d-flex align-items-start border-0 shadow-sm mb-3">
                    <i class="fa-solid fa-triangle-exclamation me-3 fs-4 text-danger mt-1"></i>
                    <div>
                      <h6 class="fw-bold mb-1">Execution Failure</h6>
                      <p class="mb-0 text-monospace small">{{ queryError }}</p>
                    </div>
                  </div>
                </transition>

                <!-- Results Section -->
                <div v-if="queryResults !== null" class="results-container mt-4">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h6 class="fw-bold text-secondary mb-0">
                      <i class="fa-solid fa-table me-2"></i>Query Results
                      <span class="badge bg-light text-dark ms-2 font-monospace" v-if="queryResults.length">
                        {{ queryResults.length }} rows in {{ queryExecutionTime ? queryExecutionTime.toFixed(2) : 0 }} ms
                      </span>
                    </h6>
                    <div class="d-flex gap-2 align-items-center" v-if="queryResults.length > 0">
                      <input 
                        type="text" 
                        class="form-control form-control-sm" 
                        v-model="filterText" 
                        placeholder="Filter rows..."
                        style="max-width: 200px;"
                      >
                      <button @click="copyResultsJSON" class="btn btn-outline-info export-btn" title="Copy raw JSON data">
                        <i class="fa-solid fa-copy"></i>
                        <span>Copy JSON</span>
                      </button>
                      <button @click="exportResultsCSV" class="btn btn-outline-success export-btn" title="Export to CSV file">
                        <i class="fa-solid fa-file-csv"></i>
                        <span>Export CSV</span>
                      </button>
                    </div>
                  </div>

                  <div v-if="queryResults.length === 0" class="text-center py-5 border rounded bg-light text-muted">
                    <i class="fa-solid fa-inbox fs-1 mb-3"></i>
                    <p class="mb-0">Query executed successfully, but returned 0 rows.</p>
                  </div>

                  <div v-else class="table-responsive border rounded shadow-sm bg-white" style="max-height: 400px; overflow-y: auto;">
                    <table class="table table-hover table-striped table-sm mb-0 align-middle">
                      <thead class="bg-light sticky-top">
                        <tr>
                          <th v-for="header in resultHeaders" :key="header" class="text-nowrap py-2 px-3 text-secondary font-monospace" style="font-size: 13px;">
                            {{ header }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, idx) in filteredResults" :key="idx">
                          <td v-for="header in resultHeaders" :key="header" class="py-2 px-3 text-monospace text-truncate font-size-xs" style="max-width: 250px;" :title="row[header]">
                            {{ formatCell(row[header]) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Tab 3: Truncate Database Data -->
    <div v-show="mainActiveTab === 'truncate'" class="tab-pane fade show active">
      <div class="row justify-content-center">
        <div class="col-lg-6 col-md-8 col-12">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pt-4 pb-0 text-center">
              <h5 class="fw-bold mb-1 text-danger">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>Truncate Database Data
              </h5>
              <p class="text-muted small mb-0">Completely remove all leads, proposals, contracts, and organisations data from the database.</p>
            </div>
            
            <div class="card-body p-4">
              <div class="alert alert-warning border-0 d-flex align-items-start mb-4" style="border-radius: 8px; background-color: #fff3cd; color: #664d03;">
                <i class="fa-solid fa-circle-exclamation fs-4 me-3 text-warning"></i>
                <div>
                  <h6 class="fw-bold mb-1">Warning: Permanent Action!</h6>
                  <p class="mb-0 small text-dark-50">This action cannot be undone. All database records for Leads, Proposals, Contracts, and Organisations will be permanently deleted (truncated).</p>
                </div>
              </div>

              <div class="mb-3">
                <label for="truncatePassword" class="form-label fw-bold text-secondary">Debugger Truncate Password</label>
                <input 
                  type="password" 
                  id="truncatePassword" 
                  class="form-control form-control-lg text-center" 
                  v-model="truncatePassword" 
                  placeholder="Enter password to confirm"
                  @keyup.enter="handleTruncateData"
                  :disabled="truncatingData"
                />
              </div>

              <button 
                @click="handleTruncateData" 
                class="btn btn-danger w-100 fw-bold py-3 mt-2 btn-action" 
                :disabled="!truncatePassword || truncatingData"
              >
                <span v-if="truncatingData">
                  <i class="fa-solid fa-spinner fa-spin me-2"></i>Truncating Database Data...
                </span>
                <span v-else>
                  <i class="fa-solid fa-trash-can me-2"></i>Truncate All Data
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 4: Xodo Error Logs -->
    <div v-show="mainActiveTab === 'logs'" class="tab-pane fade show active">
      <div class="card border-0 shadow-sm">
        <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h5 class="fw-bold mb-1" style="color: #435666;">
              <i class="fa-solid fa-file-invoice text-warning me-2"></i>Xodo Sign API Error Logs
            </h5>
            <p class="text-muted small mb-0">View recent Eversign/Xodo Sign integration error responses directly from /tmp/xodo_debug.log on the server.</p>
          </div>
          <div class="d-flex gap-2">
            <button @click="fetchXodoLogs" class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" :disabled="loadingLogs">
              <i class="fa-solid fa-sync" :class="{ 'fa-spin': loadingLogs }"></i> Refresh Logs
            </button>
            <button @click="clearXodoLogs" class="btn btn-outline-danger btn-sm d-flex align-items-center gap-1" :disabled="clearingLogs || !xodoLogs">
              <i class="fa-solid fa-trash-can"></i> Clear Logs
            </button>
          </div>
        </div>
        
        <div class="card-body p-4">
          <div v-if="loadingLogs" class="text-center py-5">
            <div class="spinner-border text-warning" role="status">
              <span class="visually-hidden">Loading logs...</span>
            </div>
            <p class="text-muted small mt-2">Fetching logs from server...</p>
          </div>
          <div v-else>
            <div v-if="xodoLogs" class="log-container bg-dark text-light p-3 rounded font-monospace" style="max-height: 500px; overflow-y: auto; white-space: pre-wrap; font-size: 13px;">
              {{ xodoLogs }}
            </div>
            <div v-else class="text-center py-5 border rounded bg-light text-muted">
              <i class="fa-solid fa-circle-check fs-1 text-success mb-3"></i>
              <p class="mb-0 fw-bold">No errors logged! Xodo Sign integration is working beautifully.</p>
              <p class="text-muted small mb-0">Errors will automatically appear here when an API call fails.</p>
            </div>
          </div>
        </div>
      </div>
    <!-- Tab 5: Auth & Security Logs -->
    <div v-show="mainActiveTab === 'authLogs'" class="tab-pane fade show active">
      <div class="card border-0 shadow-sm">
        <div class="card-header bg-white border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
          <div>
            <h5 class="fw-bold mb-1" style="color: #435666;">
              <i class="fa-solid fa-shield-halved text-info me-2"></i>Security & Authorization Logs
            </h5>
            <p class="text-muted small mb-0">View live, in-memory authorization evaluations for leads, proposals, and contracts on the staging server.</p>
          </div>
          <div class="d-flex gap-2">
            <button @click="fetchAuthLogs" class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" :disabled="loadingAuthLogs">
              <i class="fa-solid fa-sync" :class="{ 'fa-spin': loadingAuthLogs }"></i> Refresh Logs
            </button>
            <button @click="clearAuthLogs" class="btn btn-outline-danger btn-sm d-flex align-items-center gap-1" :disabled="clearingAuthLogs || !authLogs.length">
              <i class="fa-solid fa-trash-can"></i> Clear Logs
            </button>
          </div>
        </div>
        
        <div class="card-body p-4">
          <div v-if="loadingAuthLogs" class="text-center py-5">
            <div class="spinner-border text-info" role="status">
              <span class="visually-hidden">Loading logs...</span>
            </div>
            <p class="text-muted small mt-2">Fetching security logs from staging server...</p>
          </div>
          <div v-else>
            <div v-if="authLogs && authLogs.length" class="log-container bg-dark text-light p-3 rounded font-monospace" style="max-height: 500px; overflow-y: auto; font-size: 13px;">
              <div v-for="(logLine, idx) in authLogs" :key="idx" :class="{'text-success fw-bold': logLine.includes('SUCCESS'), 'text-danger fw-bold': logLine.includes('FAILED'), 'text-warning': logLine.includes('checking')}" class="py-1 border-bottom border-secondary">
                {{ logLine }}
              </div>
            </div>
            <div v-else class="text-center py-5 border rounded bg-light text-muted">
              <i class="fa-solid fa-circle-check fs-1 text-success mb-3"></i>
              <p class="mb-0 fw-bold">No security logs recorded yet!</p>
              <p class="text-muted small mb-0">Logs will automatically appear here when users access proposals or contracts.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<script>
export default {
  data() {
    return {
      proposals: [],
      contracts: [],
      selectedProposalUuid: '',
      manualProposalUuid: '',
      selectedContractUuid: '',
      manualContractUuid: '',
      loadingProposals: false,
      loadingContracts: false,
      runningAction: false,

      // Top-level tab state
      mainActiveTab: 'tests',

      // Truncate state
      truncatePassword: '',
      truncatingData: false,

      // Xodo logs state
      xodoLogs: '',
      loadingLogs: false,
      clearingLogs: false,

      // Auth logs state
      authLogs: [],
      loadingAuthLogs: false,
      clearingAuthLogs: false,

      // SQL console state
      dbQuery: 'SELECT * FROM tbl_leads LIMIT 5;',
      queryResults: null,
      queryError: '',
      queryExecutionTime: null,
      runningQuery: false,
      dbSchema: [],
      loadingSchema: false,
      filterText: '',
      expandedTables: {},

      // Visual builder state
      activeTab: 'visual',
      selectedTable: '',
      selectedColumns: [],
      queryFilters: [],
      sortByColumn: '',
      sortOrder: 'DESC',
      queryLimit: 10,
    };
  },

  computed: {
    activeProposalUuid() {
      return this.manualProposalUuid.trim() || this.selectedProposalUuid;
    },
    activeContractUuid() {
      return this.manualContractUuid.trim() || this.selectedContractUuid;
    },
    activeProposal() {
      const uuid = this.activeProposalUuid;
      if (!uuid) return null;
      return this.proposals.find(p => p.id === uuid) || { id: uuid, idOnly: true };
    },
    activeContract() {
      const uuid = this.activeContractUuid;
      if (!uuid) return null;
      return this.contracts.find(c => c.id === uuid) || { id: uuid, idOnly: true };
    },
    resultHeaders() {
      if (!this.queryResults || !this.queryResults.length) return [];
      return Object.keys(this.queryResults[0]);
    },
    filteredResults() {
      if (!this.queryResults) return [];
      if (!this.filterText.trim()) return this.queryResults;
      const query = this.filterText.toLowerCase();
      return this.queryResults.filter(row => {
        return Object.values(row).some(val => 
          ('' + (val || '')).toLowerCase().includes(query)
        );
      });
    },
    generatedSql() {
      if (!this.selectedTable) return '';
      
      let columns = '*';
      const selectedCols = this.selectedColumns.filter(c => c.checked).map(c => c.name);
      if (selectedCols.length > 0 && selectedCols.length < this.selectedColumns.length) {
        columns = selectedCols.map(c => `\`${c}\``).join(', ');
      }
      
      let query = `SELECT ${columns} FROM \`${this.selectedTable}\``;
      
      const activeFilters = this.queryFilters.filter(f => f.column && f.operator);
      if (activeFilters.length > 0) {
        const filterClauses = activeFilters.map(f => {
          const col = `\`${f.column}\``;
          if (f.operator === 'IS NULL' || f.operator === 'IS NOT NULL') {
            return `${col} ${f.operator}`;
          }
          
          let val = f.value;
          if (f.operator === 'LIKE') {
            if (!val.includes('%')) {
              val = `%${val}%`;
            }
          }
          const escapedVal = ('' + val).replace(/'/g, "\\'");
          return `${col} ${f.operator} '${escapedVal}'`;
        });
        query += ` WHERE ${filterClauses.join(' AND ')}`;
      }
      
      if (this.sortByColumn) {
        query += ` ORDER BY \`${this.sortByColumn}\` ${this.sortOrder}`;
      }
      
      if (this.queryLimit) {
        query += ` LIMIT ${parseInt(this.queryLimit, 10) || 10}`;
      }
      
      return query + ';';
    },
  },

  created() {
    const isSuperAdmin = this.$store.state.auth.isSuperAdmin;
    const isAdmin = this.$store.state.auth.isAdmin;
    const isConsultant = this.$store.state.auth.isConsultant;
    if (!isSuperAdmin || isAdmin || isConsultant) {
      this.$router.push("/dashboard");
      return;
    }
    this.init();
  },

  methods: {
    init() {
      this.loadProposals();
      this.loadContracts();
      this.loadSchema();
    },

    async loadProposals() {
      this.loadingProposals = true;
      try {
        const response = await this.$axios.get('/api/debugger/proposals');
        if (response.data && response.data.success) {
          this.proposals = response.data.proposals || [];
        }
      } catch (err) {
        console.error('Failed to load proposals:', err);
        this.$toast.error('Failed to fetch proposals list for debugger dropdown');
      } finally {
        this.loadingProposals = false;
      }
    },

    async loadContracts() {
      this.loadingContracts = true;
      try {
        const response = await this.$axios.get('/api/debugger/contracts');
        if (response.data && response.data.success) {
          this.contracts = response.data.contracts || [];
        }
      } catch (err) {
        console.error('Failed to load contracts:', err);
        this.$toast.error('Failed to fetch contracts list for debugger dropdown');
      } finally {
        this.loadingContracts = false;
      }
    },

    onProposalChange() {
      if (this.selectedProposalUuid) {
        this.manualProposalUuid = '';
      }
    },

    onManualProposalInput() {
      if (this.manualProposalUuid.trim()) {
        this.selectedProposalUuid = '';
      }
    },

    onContractChange() {
      if (this.selectedContractUuid) {
        this.manualContractUuid = '';
      }
    },

    onManualContractInput() {
      if (this.manualContractUuid.trim()) {
        this.selectedContractUuid = '';
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    getContractStatusText(status) {
      switch (status) {
        case 0: return 'Draft';
        case 1: return 'Sent';
        case 2: return 'Viewed';
        case 3: return 'In Review';
        case 4: return 'Executed';
        case 5: return 'Cancelled';
        case 6: return 'Pending Approval';
        case 7: return 'Approved';
        case 8: return 'Declined';
        case 9: return 'Expired';
        default: return `Status ${status}`;
      }
    },

    getContractStatusClass(status) {
      switch (status) {
        case 0: return 'bg-secondary';
        case 1: return 'bg-info text-dark';
        case 2: return 'bg-primary';
        case 3: return 'bg-warning text-dark';
        case 4: return 'bg-success';
        case 5: return 'bg-danger';
        case 6: return 'bg-purple text-dark';
        case 7: return 'bg-success';
        case 8: return 'bg-danger';
        case 9: return 'bg-danger';
        default: return 'bg-light text-dark';
      }
    },

    getContractSigners(contract) {
      if (!contract) return [];
      let signers = [];
      const seenEmails = new Set();
      // Pull signers from contract acceptances (the actual signer records)
      const acc = contract.contract_acceptance || (contract.contract_acceptances && contract.contract_acceptances[contract.contract_acceptances.length - 1]);
      if (acc && acc.signers && acc.signers.length > 0) {
        acc.signers.forEach(s => {
          const email = (s.email || '').toLowerCase();
          if (email && !seenEmails.has(email)) {
            seenEmails.add(email);
            const first = s.first_name || s.firstName || '';
            const last = s.last_name || s.lastName || '';
            const name = [first, last].filter(Boolean).join(' ') || s.name || s.email;
            signers.push({
              first_name: first,
              last_name: last,
              firstName: first,
              lastName: last,
              name: name,
              email: s.email,
              role: s.role
            });
          }
        });
      }
      // Fallback to shared users if no acceptance signers found
      if (signers.length === 0) {
        if (contract.shared_users && contract.shared_users.length > 0) {
          contract.shared_users.forEach(su => {
            const email = (su.email || '').toLowerCase();
            if (su.share_type === 'signer' && email && !seenEmails.has(email)) {
              seenEmails.add(email);
              signers.push({
                first_name: '',
                last_name: '',
                firstName: '',
                lastName: '',
                name: su.email,
                email: su.email,
                role: 'Signatory'
              });
            }
          });
        }
        // Final fallback to lead email
        if (signers.length === 0 && contract.Lead && contract.Lead.email) {
          const first = contract.Lead.first_name || '';
          const last = contract.Lead.last_name || '';
          const name = [first, last].filter(Boolean).join(' ') || contract.Lead.contact_name || contract.Lead.email;
          signers.push({
            first_name: first,
            last_name: last,
            firstName: first,
            lastName: last,
            name: name,
            email: contract.Lead.email,
            role: 'Primary Signatory'
          });
        }
      }
      return signers;
    },

    getProposalPrimaryContact(proposal) {
      if (!proposal) return '';
      const acc = proposal.contract_acceptance || (proposal.contract_acceptances && proposal.contract_acceptances[proposal.contract_acceptances.length - 1]);
      if (acc) {
        const first = acc.primary_contact_first_name || '';
        const last = acc.primary_contact_last_name || '';
        const name = [first, last].filter(Boolean).join(' ') || acc.primary_contact_name;
        if (name) return name;
      }
      if (proposal.Lead) {
        const first = proposal.Lead.first_name || '';
        const last = proposal.Lead.last_name || '';
        const name = [first, last].filter(Boolean).join(' ') || proposal.Lead.contact_name;
        if (name) return name;
      }
      return '';
    },

    getContractPrimaryContact(contract) {
      if (!contract) return '';
      const acc = contract.contract_acceptance || (contract.contract_acceptances && contract.contract_acceptances[contract.contract_acceptances.length - 1]);
      if (acc) {
        const first = acc.primary_contact_first_name || '';
        const last = acc.primary_contact_last_name || '';
        const name = [first, last].filter(Boolean).join(' ') || acc.primary_contact_name;
        if (name) return name;
      }
      if (contract.Lead) {
        const first = contract.Lead.first_name || '';
        const last = contract.Lead.last_name || '';
        const name = [first, last].filter(Boolean).join(' ') || contract.Lead.contact_name;
        if (name) return name;
      }
      return '';
    },

    getProposalAcceptanceOrg(proposal) {
      if (!proposal) return '';
      if (proposal.contract_acceptance && proposal.contract_acceptance.organisation_name) {
        return proposal.contract_acceptance.organisation_name.trim();
      }
      if (proposal.contract_acceptances && proposal.contract_acceptances.length) {
        const latest = proposal.contract_acceptances[proposal.contract_acceptances.length - 1];
        return latest && latest.organisation_name ? latest.organisation_name.trim() : '';
      }
      return '';
    },

    getContractAcceptanceOrg(contract) {
      if (!contract) return '';
      if (contract.contract_acceptance && contract.contract_acceptance.organisation_name) {
        return contract.contract_acceptance.organisation_name.trim();
      }
      return '';
    },

    // Action Endpoints
    async prepareProposalReminder() {
      this.runningAction = true;
      try {
        const response = await this.$axios.post('/api/debugger/proposal/reminder', {
          uuid: this.activeProposalUuid,
        });
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message);
          this.loadProposals();
        } else {
          this.$toast.error(response.data.message || 'Action failed');
        }
      } catch (err) {
        this.$toast.error('Network request failed: ' + (err.response?.data?.message || err.message));
      } finally {
        this.runningAction = false;
      }
    },

    async prepareProposalExpiry() {
      this.runningAction = true;
      try {
        const response = await this.$axios.post('/api/debugger/proposal/expiry', {
          uuid: this.activeProposalUuid,
        });
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message);
          this.loadProposals();
        } else {
          this.$toast.error(response.data.message || 'Action failed');
        }
      } catch (err) {
        this.$toast.error('Network request failed: ' + (err.response?.data?.message || err.message));
      } finally {
        this.runningAction = false;
      }
    },

    async resetProposal() {
      this.runningAction = true;
      try {
        const response = await this.$axios.post('/api/debugger/proposal/reset', {
          uuid: this.activeProposalUuid,
        });
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message);
          this.loadProposals();
        } else {
          this.$toast.error(response.data.message || 'Action failed');
        }
      } catch (err) {
        this.$toast.error('Network request failed: ' + (err.response?.data?.message || err.message));
      } finally {
        this.runningAction = false;
      }
    },

    async prepareContractReminder() {
      this.runningAction = true;
      try {
        const response = await this.$axios.post('/api/debugger/contract/reminder', {
          uuid: this.activeContractUuid,
        });
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message);
          this.loadContracts();
        } else {
          this.$toast.error(response.data.message || 'Action failed');
        }
      } catch (err) {
        this.$toast.error('Network request failed: ' + (err.response?.data?.message || err.message));
      } finally {
        this.runningAction = false;
      }
    },

    async resetContract() {
      this.runningAction = true;
      try {
        const response = await this.$axios.post('/api/debugger/contract/reset', {
          uuid: this.activeContractUuid,
        });
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message);
          this.loadContracts();
        } else {
          this.$toast.error(response.data.message || 'Action failed');
        }
      } catch (err) {
        this.$toast.error('Network request failed: ' + (err.response?.data?.message || err.message));
      } finally {
        this.runningAction = false;
      }
    },

    // SQL Select Tool Methods
    async loadSchema() {
      this.loadingSchema = true;
      try {
        const response = await this.$axios.get('/api/debugger/schema');
        if (response.data && response.data.success) {
          this.dbSchema = response.data.tables || [];
          // Initialize expanded states
          const expanded = {};
          this.dbSchema.forEach(table => {
            expanded[table.name] = false;
          });
          this.expandedTables = expanded;
        }
      } catch (err) {
        console.error('Failed to load database schema:', err);
        this.$toast.error('Failed to load database schema for explorer');
      } finally {
        this.loadingSchema = false;
      }
    },

    toggleTable(tableName) {
      this.expandedTables = {
        ...this.expandedTables,
        [tableName]: !this.expandedTables[tableName]
      };
    },

    quickQuery(tableName) {
      // Toggle to advanced tab and set query
      this.activeTab = 'advanced';
      this.dbQuery = `SELECT * FROM ${tableName} LIMIT 10;`;
      this.runQuery();
    },

    insertAtCursor(text) {
      if (this.activeTab === 'visual') {
        // If they click in the schema explorer while in Visual mode, select that table!
        // Find which table this column belongs to
        const parentTable = this.dbSchema.find(t => t.columns?.some(c => c.name === text));
        if (parentTable) {
          if (this.selectedTable !== parentTable.name) {
            this.selectedTable = parentTable.name;
            this.onTableChange();
          }
          // Check this column
          const col = this.selectedColumns.find(c => c.name === text);
          if (col) {
            col.checked = true;
          }
        }
        return;
      }

      const textarea = this.$refs.sqlEditor;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const val = this.dbQuery;
        this.dbQuery = val.substring(0, start) + text + val.substring(end);
        this.$nextTick(() => {
          textarea.focus();
          textarea.setSelectionRange(start + text.length, start + text.length);
        });
      } else {
        this.dbQuery += ' ' + text;
      }
    },

    setTemplate(type) {
      switch (type) {
        case 'leads':
          this.dbQuery = 'SELECT id, organisation_name, contact_name, email, phone, created_at FROM tbl_leads ORDER BY created_at DESC LIMIT 10;';
          break;
        case 'proposals':
          this.dbQuery = 'SELECT id, lead_id, proposal_owner, grand_total, is_approved, expiry_date FROM tbl_proposals WHERE is_approved = 1 ORDER BY created_at DESC LIMIT 10;';
          break;
        case 'contracts':
          this.dbQuery = 'SELECT id, lead_id, contract_status, grand_total, next_reminder_at FROM tbl_contracts WHERE contract_status = 4 ORDER BY created_at DESC LIMIT 10;';
          break;
        case 'count':
          this.dbQuery = 'SELECT lead_status_id, COUNT(*) as total_leads FROM tbl_leads GROUP BY lead_status_id ORDER BY total_leads DESC;';
          break;
      }
    },

    async runQuery() {
      let queryToRun = '';
      if (this.activeTab === 'visual') {
        if (!this.selectedTable) {
          this.$toast.error('Please select a table first');
          return;
        }
        queryToRun = this.generatedSql;
        this.dbQuery = queryToRun; // Sync to textarea too
      } else {
        queryToRun = this.dbQuery;
      }

      if (!queryToRun.trim()) return;
      this.runningQuery = true;
      this.queryError = '';
      this.queryResults = null;
      this.queryExecutionTime = null;
      try {
        const response = await this.$axios.post('/api/debugger/query', {
          query: queryToRun,
        });
        if (response.data && response.data.success) {
          this.queryResults = response.data.results || [];
          this.queryExecutionTime = response.data.execution_time_ms;
        } else {
          this.queryError = response.data.message || 'Failed to execute query';
        }
      } catch (err) {
        console.error('Failed to run query:', err);
        this.queryError = err.response?.data?.message || err.message || 'Network request failed';
      } finally {
        this.runningQuery = false;
      }
    },

    clearQuery() {
      this.dbQuery = '';
      this.queryResults = null;
      this.queryError = '';
      this.queryExecutionTime = null;
    },

    formatCell(val) {
      if (val === null || val === undefined) return 'NULL';
      if (typeof val === 'object') return JSON.stringify(val);
      if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';
      return val;
    },

    copyResultsJSON() {
      if (!this.queryResults) return;
      try {
        const jsonText = JSON.stringify(this.queryResults, null, 2);
        navigator.clipboard.writeText(jsonText);
        this.$toast.success('Results JSON copied to clipboard!');
      } catch (err) {
        this.$toast.error('Failed to copy to clipboard');
      }
    },

    exportResultsCSV() {
      if (!this.queryResults || !this.queryResults.length) return;
      try {
        const headers = this.resultHeaders;
        const csvRows = [];
        // headers row
        csvRows.push(headers.join(','));
        // data rows
        for (const row of this.queryResults) {
          const values = headers.map(header => {
            const val = row[header];
            const escaped = ('' + (val === null || val === undefined ? '' : val)).replace(/"/g, '""');
            return `"${escaped}"`;
          });
          csvRows.push(values.join(','));
        }
        
        const blob = new Blob([csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `spark_query_export_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.$toast.success('CSV export started!');
      } catch (err) {
        this.$toast.error('CSV export failed: ' + err.message);
      }
    },

    // Visual Builder Methods
    onTableChange() {
      const table = this.dbSchema.find(t => t.name === this.selectedTable);
      if (table) {
        this.selectedColumns = (table.columns || []).map(col => ({
          name: col.name,
          type: col.type,
          checked: true
        }));
        this.sortByColumn = (table.columns || []).find(col => col.name === 'created_at' || col.name === 'id')?.name || '';
      } else {
        this.selectedColumns = [];
        this.sortByColumn = '';
      }
      this.queryFilters = [];
      this.sortOrder = 'DESC';
      this.queryLimit = 10;
    },

    selectAllColumns(checked) {
      this.selectedColumns.forEach(col => {
        col.checked = checked;
      });
    },

    addFilter() {
      this.queryFilters.push({
        column: '',
        operator: '=',
        value: ''
      });
    },

    removeFilter(idx) {
      this.queryFilters.splice(idx, 1);
    },

    resetVisualBuilder() {
      this.onTableChange();
    },

    async handleTruncateData() {
      if (!this.truncatePassword) {
        this.$toast.error('Password is required');
        return;
      }
      this.truncatingData = true;
      try {
        const response = await this.$axios.post('/api/debugger/truncate', {
          password: this.truncatePassword
        });
        if (response.data && response.data.success) {
          this.$toast.success(response.data.message || 'Data successfully truncated!');
          this.truncatePassword = '';
        } else {
          this.$toast.error(response.data.message || 'Failed to truncate data');
        }
      } catch (err) {
        console.error('Truncate data failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Truncation request failed');
      } finally {
        this.truncatingData = false;
      }
    },

    async fetchXodoLogs() {
      this.loadingLogs = true;
      try {
        const response = await this.$axios.get('/api/debugger/xodo-logs');
        if (response.data && response.data.success) {
          this.xodoLogs = response.data.logs || '';
        } else {
          this.$toast.error(response.data.message || 'Failed to fetch Xodo logs');
        }
      } catch (err) {
        console.error('Fetch Xodo logs failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to fetch logs');
      } finally {
        this.loadingLogs = false;
      }
    },

    async clearXodoLogs() {
      if (!confirm('Are you sure you want to clear the Xodo error logs? This will erase /tmp/xodo_debug.log.')) {
        return;
      }
      this.clearingLogs = true;
      try {
        const response = await this.$axios.post('/api/debugger/xodo-logs/clear');
        if (response.data && response.data.success) {
          this.$toast.success('Logs cleared successfully');
          this.xodoLogs = '';
        } else {
          this.$toast.error(response.data.message || 'Failed to clear logs');
        }
      } catch (err) {
        console.error('Clear Xodo logs failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to clear logs');
      } finally {
        this.clearingLogs = false;
      }
    },

    async fetchAuthLogs() {
      this.loadingAuthLogs = true;
      try {
        const response = await this.$axios.get('/api/debugger/status');
        if (response.data && response.data.success) {
          this.authLogs = response.data.auth_logs || [];
        } else {
          this.$toast.error(response.data.message || 'Failed to fetch security logs');
        }
      } catch (err) {
        console.error('Fetch auth logs failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to fetch security logs');
      } finally {
        this.loadingAuthLogs = false;
      }
    },

    async clearAuthLogs() {
      if (!confirm('Are you sure you want to clear the security & authorization logs?')) {
        return;
      }
      this.clearingAuthLogs = true;
      try {
        const response = await this.$axios.get('/api/debugger/status?clear=true');
        if (response.data && response.data.success) {
          this.$toast.success('Security logs cleared successfully');
          this.authLogs = [];
        } else {
          this.$toast.error(response.data.message || 'Failed to clear security logs');
        }
      } catch (err) {
        console.error('Clear auth logs failed:', err);
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to clear security logs');
      } finally {
        this.clearingAuthLogs = false;
      }
    },
  },
};
</script>

<style scoped>
.debugger-page {
  max-width: 100%;
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.details-box {
  background-color: #f7f9fa;
  border-left: 4px solid #00a0c1;
  transition: all 0.3s ease;
}

.btn-action {
  transition: all 0.2s ease;
  border-radius: 8px;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* SQL Select Tool Styles */
.cursor-pointer {
  cursor: pointer;
}

.hover-bg-light:hover {
  background-color: #f8f9fa;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #e9ecef;
}

.font-size-xs {
  font-size: 11px;
}

.font-italic {
  font-style: italic;
}

.sql-editor {
  background-color: #1e1e1e;
  color: #d4d4d4;
  border: 1px solid #3c3c3c;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  border-radius: 8px;
  padding: 12px;
  resize: vertical;
  transition: all 0.2s ease;
}

.sql-editor:focus {
  background-color: #1a1a1a;
  color: #ffffff;
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
  outline: none;
}

.schema-list {
  background-color: #fafbfc;
  border: 1px solid #e1e4e6;
  border-radius: 8px;
  padding: 12px;
}

.schema-list::-webkit-scrollbar {
  width: 6px;
}

.schema-list::-webkit-scrollbar-thumb {
  background-color: #c1c9d0;
  border-radius: 3px;
}

.table-node {
  transition: all 0.2s ease;
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: max-height 0.25s ease-in-out, opacity 0.25s ease-in-out;
  max-height: 500px;
  overflow: hidden;
}

.slide-down-enter-from, .slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

.results-container {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}

.table-responsive::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}

/* Overrides to prevent global circular styling on select builder and export buttons */
.sort-order-btn {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-top-right-radius: 10px !important;
  border-bottom-right-radius: 10px !important;
  width: auto !important;
  height: auto !important;
  padding: 0.375rem 1rem !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 6px !important;
  font-weight: bold !important;
  line-height: 1.5 !important;
}

.export-btn {
  width: auto !important;
  height: auto !important;
  padding: 6px 16px !important;
  border-radius: 30px !important;
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  white-space: nowrap !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: all 0.2s ease !important;
}

.export-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

/* Top-Level Tabs Custom Styling */
.nav-tabs {
  border-bottom: 2px solid #e9ecef !important;
}
.nav-tabs .nav-link {
  border: none !important;
  color: #6c757d !important;
  background: transparent !important;
  transition: all 0.2s ease;
  position: relative;
  padding: 12px 24px !important;
}
.nav-tabs .nav-link:hover {
  color: #00a0c1 !important;
}
.nav-tabs .nav-link.active {
  color: #00a0c1 !important;
  background: transparent !important;
}
.nav-tabs .nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #00a0c1;
  border-radius: 3px;
}
</style>
